import React, { useRef, useState } from 'react';
import { Stethoscope, UploadCloud, Loader2, Car, User, FileText, AlertTriangle, CheckCircle2, ArrowRight, Sparkles, ShieldCheck, Save, X, Trash2, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { carsysApi } from '../api/carsysApi';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';

const ERNST = {
  hoog: { bg: '#fef2f2', c: '#b91c1c', b: '#fecaca', l: 'Hoog' },
  gemiddeld: { bg: '#fffbeb', c: '#b45309', b: '#fde68a', l: 'Gemiddeld' },
  laag: { bg: '#f0fdf4', c: '#15803d', b: '#bbf7d0', l: 'Laag' },
};

const CONF = {
  hoog: { c: '#15803d', bg: '#f0fdf4', b: '#bbf7d0', l: 'Zeker' },
  gemiddeld: { c: '#b45309', bg: '#fffbeb', b: '#fde68a', l: 'Afgeleid' },
  laag: { c: '#b91c1c', bg: '#fef2f2', b: '#fecaca', l: 'Check' },
};

export default function Diagnose({ onNavigate }) {
  const { t } = useT();
  const { refreshAll } = useData();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [phase, setPhase] = useState('upload'); // upload | review | saved
  const [draftId, setDraftId] = useState(null);
  const [diag, setDiag] = useState(null);       // bewerkbaar voorstel
  const [voorstel, setVoorstel] = useState(null);
  const [saved, setSaved] = useState(null);     // resultaat na confirm

  const reset = () => {
    setPhase('upload'); setDraftId(null); setDiag(null); setVoorstel(null); setSaved(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('Alleen PDF-bestanden', 'Alleen PDF-bestanden worden ondersteund'));
      return;
    }
    setBusy(true);
    const tid = toast.loading(t('AI analyseert het diagnoserapport...', 'AI analyseert het diagnoserapport...'));
    try {
      const res = await carsysApi.analyseDiagnose(file);
      setDraftId(res.draftId);
      setDiag(res.diagnose || {});
      setVoorstel(res.voorstel || {});
      setPhase('review');
      toast.success(t('Analyse klaar — controleer voor opslaan', 'Analyse klaar — controleer voor opslaan'), {
        id: tid,
        duration: 2500,
        description: `${(res.diagnose?.dtcs || []).length} ${t('foutcode(s) gevonden', 'foutcode(s) gevonden')}`,
      });
    } catch (e) {
      toast.error(t('Analyse mislukt', 'Analyse mislukt'), { id: tid, description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (!draftId || !diag) return;
    setBusy(true);
    const tid = toast.loading(t('Diagnose definitief vastleggen...', 'Diagnose definitief vastleggen...'));
    try {
      const res = await carsysApi.confirmDiagnose({ draftId, diagnose: diag });
      setSaved(res);
      setPhase('saved');
      toast.success(t('Diagnose opgeslagen', 'Diagnose opgeslagen'), {
        id: tid,
        description: `${res.bon?.nummer || ''} · ${res.klant?.naam || ''}`,
      });
      refreshAll(true);
    } catch (e) {
      toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { id: tid, description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (busy) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  // helpers voor bewerken
  const setField = (k, v) => setDiag((d) => ({ ...d, [k]: v }));
  const setDtc = (i, k, v) => setDiag((d) => {
    const dtcs = [...(d.dtcs || [])];
    dtcs[i] = { ...dtcs[i], [k]: v };
    return { ...d, dtcs };
  });
  const removeDtc = (i) => setDiag((d) => ({ ...d, dtcs: (d.dtcs || []).filter((_, idx) => idx !== i) }));
  const addDtc = () => setDiag((d) => ({ ...d, dtcs: [...(d.dtcs || []), { code: '', systeem: '', omschrijving: '', uitleg: '', ernst: 'gemiddeld', advies: '' }] }));

  return (
    <div className="space-y-6" data-testid="diagnose-page">
      <PageHeader pageKey="diagnose" title={t('AI Diagnose', 'AI Diagnose')}
        subtitle={t('Upload een diagnoserapport (PDF) — AI maakt een voorstel dat je eerst controleert vóór het opslaan', 'Upload een diagnoserapport (PDF) — AI maakt een voorstel dat je eerst controleert vóór het opslaan')} />

      {/* ===================== UPLOAD ===================== */}
      {phase === 'upload' && (
        <div
          data-testid="diagnose-dropzone"
          onClick={() => !busy && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          style={{
            border: `2px dashed ${drag ? '#2563eb' : '#cbd5e1'}`,
            background: drag ? '#eff6ff' : '#f8fafc',
            borderRadius: 16, padding: '40px 24px', textAlign: 'center',
            cursor: busy ? 'wait' : 'pointer', transition: 'border-color 150ms ease, background-color 150ms ease',
          }}
        >
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
            data-testid="diagnose-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; handleFile(f); }} />
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, borderRadius: 18, background: 'linear-gradient(135deg,#2563eb,#1e40af)', color: '#fff', marginBottom: 14 }}>
            {busy ? <Loader2 size={30} className="diag-spin" /> : <UploadCloud size={30} />}
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
            {busy ? t('AI analyseert het rapport...', 'AI analyseert het rapport...') : t('Sleep je diagnoserapport hierheen', 'Sleep je diagnoserapport hierheen')}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
            {busy ? t('Dit duurt enkele seconden', 'Dit duurt enkele seconden') : t('of klik om een PDF te kiezen (max 15 MB)', 'of klik om een PDF te kiezen (max 15 MB)')}
          </div>
          {!busy && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#2563eb', marginTop: 14 }}>
              <Sparkles size={13} /> {t('Werkt met Autel en andere OBD-diagnoserapporten', 'Werkt met Autel en andere OBD-diagnoserapporten')}
            </div>
          )}
        </div>
      )}

      {/* ===================== REVIEW (Controleer voor opslaan) ===================== */}
      {phase === 'review' && diag && (
        <div className="space-y-4" data-testid="diagnose-review">
          <div data-testid="diagnose-review-banner" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck size={20} color="#1e40af" />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#1e40af' }}>{t('Controleer voor opslaan', 'Controleer voor opslaan')}</div>
              <div style={{ fontSize: 13, color: '#475569' }}>{t('AI heeft de gegevens uitgelezen. Controleer en pas waar nodig aan — er wordt nog niets opgeslagen.', 'AI heeft de gegevens uitgelezen. Controleer en pas waar nodig aan — er wordt nog niets opgeslagen.')}</div>
            </div>
          </div>

          {/* Voertuig + Klant bewerkbaar */}
          <div className="cs-card" data-testid="diagnose-edit-voertuig" style={{ padding: 18 }}>
            <SectionTitle icon={Car}>{t('Voertuig', 'Voertuig')}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <Field label={t('Kenteken', 'Kenteken')} value={diag.kenteken} conf={diag.confidence?.kenteken} onChange={(v) => setField('kenteken', v)} testid="diag-kenteken" />
              <Field label={t('Merk', 'Merk')} value={diag.merk} conf={diag.confidence?.merk} onChange={(v) => setField('merk', v)} testid="diag-merk" />
              <Field label={t('Model', 'Model')} value={diag.model} conf={diag.confidence?.model} onChange={(v) => setField('model', v)} testid="diag-model" />
              <Field label={t('Bouwjaar', 'Bouwjaar')} value={diag.bouwjaar} conf={diag.confidence?.bouwjaar} onChange={(v) => setField('bouwjaar', v)} testid="diag-bouwjaar" />
              <Field label="VIN" value={diag.vin} conf={diag.confidence?.vin} onChange={(v) => setField('vin', v)} testid="diag-vin" />
              <Field label={t('Km-stand', 'Km-stand')} value={diag.kilometerstand} onChange={(v) => setField('kilometerstand', v)} testid="diag-km" />
            </div>
            {voorstel?.voertuig?.bestaat && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#15803d', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={14} /> {t('Dit voertuig bestaat al — er wordt geen dubbele aangemaakt', 'Dit voertuig bestaat al — er wordt geen dubbele aangemaakt')}
              </div>
            )}
          </div>

          <div className="cs-card" data-testid="diagnose-edit-klant" style={{ padding: 18 }}>
            <SectionTitle icon={User}>{t('Klant', 'Klant')}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <Field label={t('Naam', 'Naam')} value={diag.klantNaam} conf={diag.confidence?.klantNaam} onChange={(v) => setField('klantNaam', v)} testid="diag-klantnaam" />
            </div>
            {voorstel?.klant?.bestaat
              ? <div style={{ marginTop: 10, fontSize: 12, color: '#15803d', display: 'inline-flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={14} /> {t('Bestaande klant gevonden — wordt gekoppeld', 'Bestaande klant gevonden — wordt gekoppeld')}</div>
              : <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>{t('Nieuwe klant wordt aangemaakt', 'Nieuwe klant wordt aangemaakt')}</div>}
          </div>

          {/* Samenvatting */}
          <div className="cs-card" data-testid="diagnose-edit-samenvatting" style={{ padding: 18 }}>
            <SectionTitle icon={Stethoscope}>{t('Samenvatting', 'Samenvatting')}</SectionTitle>
            <textarea data-testid="diag-samenvatting" value={diag.samenvatting || ''} onChange={(e) => setField('samenvatting', e.target.value)}
              rows={3} style={inputStyle} placeholder={t('Korte samenvatting voor de klant', 'Korte samenvatting voor de klant')} />
          </div>

          {/* Foutcodes bewerkbaar */}
          <div className="cs-card" data-testid="diagnose-edit-dtcs" style={{ padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <SectionTitle icon={AlertTriangle}>{t('Foutcodes', 'Foutcodes')} ({(diag.dtcs || []).length})</SectionTitle>
              <button onClick={addDtc} data-testid="diag-add-dtc" style={ghostBtn}><Plus size={14} /> {t('Toevoegen', 'Toevoegen')}</button>
            </div>
            {(diag.dtcs || []).length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <CheckCircle2 size={18} /> {t('Geen foutcodes', 'Geen foutcodes')}
              </div>
            ) : (
              <div className="space-y-3">
                {(diag.dtcs || []).map((c, i) => {
                  const e = ERNST[(c.ernst || '').toLowerCase()] || ERNST.gemiddeld;
                  return (
                    <div key={i} data-testid={`diag-dtc-${i}`} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fafbfc' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 130px 36px', gap: 8, alignItems: 'center' }}>
                        <input value={c.code || ''} onChange={(ev) => setDtc(i, 'code', ev.target.value)} placeholder={t('Code', 'Code')}
                          data-testid={`diag-dtc-code-${i}`} style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontWeight: 700 }} />
                        <input value={c.omschrijving || ''} onChange={(ev) => setDtc(i, 'omschrijving', ev.target.value)} placeholder={t('Omschrijving', 'Omschrijving')}
                          data-testid={`diag-dtc-oms-${i}`} style={inputStyle} />
                        <select value={c.ernst || 'gemiddeld'} onChange={(ev) => setDtc(i, 'ernst', ev.target.value)}
                          data-testid={`diag-dtc-ernst-${i}`} style={{ ...inputStyle, background: e.bg, color: e.c, borderColor: e.b, fontWeight: 700 }}>
                          <option value="hoog">{t('Hoog', 'Hoog')}</option>
                          <option value="gemiddeld">{t('Gemiddeld', 'Gemiddeld')}</option>
                          <option value="laag">{t('Laag', 'Laag')}</option>
                        </select>
                        <button onClick={() => removeDtc(i)} data-testid={`diag-dtc-del-${i}`} title={t('Verwijderen', 'Verwijderen')}
                          style={{ ...iconBtn, color: '#b91c1c' }}><Trash2 size={15} /></button>
                      </div>
                      {c.uitleg && <div style={{ fontSize: 12.5, color: '#475569', marginTop: 8, lineHeight: 1.5 }}>{c.uitleg}</div>}
                      {c.advies && <div style={{ fontSize: 12.5, color: '#2563eb', marginTop: 4 }}><strong>{t('Advies', 'Advies')}:</strong> {c.advies}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Aanbevolen werkzaamheden (readonly) */}
          {(diag.aanbevolenWerkzaamheden || []).length > 0 && (
            <div className="cs-card" data-testid="diagnose-aanbevelingen" style={{ padding: 18 }}>
              <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>{t('Aanbevolen werkzaamheden', 'Aanbevolen werkzaamheden')}</div>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#334155', fontSize: 13.5, lineHeight: 1.8 }}>
                {diag.aanbevolenWerkzaamheden.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {/* Actiebalk */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: 'linear-gradient(180deg, rgba(248,250,252,0) 0%, #f8fafc 40%)', padding: '12px 0' }}>
            <button onClick={reset} disabled={busy} data-testid="diagnose-cancel-btn" style={secondaryBtn}>
              <X size={16} /> {t('Annuleren', 'Annuleren')}
            </button>
            <button onClick={onConfirm} disabled={busy} data-testid="diagnose-confirm-btn" style={primaryBtn}>
              {busy ? <Loader2 size={16} className="diag-spin" /> : <Save size={16} />} {t('Opslaan & werkbon maken', 'Opslaan & werkbon maken')}
            </button>
          </div>
        </div>
      )}

      {/* ===================== SAVED ===================== */}
      {phase === 'saved' && saved && (
        <div className="space-y-4" data-testid="diagnose-saved">
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle2 size={20} color="#15803d" />
            <div style={{ flex: 1, fontWeight: 700, color: '#15803d' }}>{t('Diagnose opgeslagen', 'Diagnose opgeslagen')}</div>
            <button onClick={reset} data-testid="diagnose-new-btn" style={ghostBtn}><RefreshCw size={14} /> {t('Nieuwe diagnose', 'Nieuwe diagnose')}</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14 }}>
            <InfoCard icon={Car} title={t('Voertuig', 'Voertuig')} testid="saved-voertuig">
              <strong>{saved.voertuig?.kenteken || saved.diagnose?.kenteken || '—'}</strong>
              <div style={{ color: '#64748b', fontSize: 13 }}>{[saved.diagnose?.merk, saved.diagnose?.model, saved.diagnose?.bouwjaar].filter(Boolean).join(' · ') || t('Geen voertuigdata', 'Geen voertuigdata')}</div>
            </InfoCard>
            <InfoCard icon={User} title={t('Klant', 'Klant')} testid="saved-klant">
              <strong>{saved.klant?.naam || '—'}</strong>
              <div style={{ color: '#64748b', fontSize: 13 }}>{saved.klant?.email || saved.klant?.telefoon || t('Aangemaakt via diagnose', 'Aangemaakt via diagnose')}</div>
            </InfoCard>
            <InfoCard icon={FileText} title={t('Werkbon', 'Werkbon')} testid="saved-bon">
              <strong>{saved.bon?.nummer || '—'}</strong>
              <div style={{ color: '#64748b', fontSize: 13 }}>{(saved.diagnose?.dtcs || []).length} {t('regel(s)', 'regel(s)')}</div>
              <button onClick={() => onNavigate && onNavigate('bonnen')} data-testid="diagnose-open-bonnen"
                style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {t('Open in Bonnen', 'Open in Bonnen')} <ArrowRight size={13} />
              </button>
            </InfoCard>
          </div>
        </div>
      )}

      <style>{`.diag-spin { animation: diagspin 1s linear infinite; } @keyframes diagspin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13.5, color: '#0f172a', outline: 'none', background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', border: 'none', borderRadius: 10, background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const secondaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '10px 18px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#2563eb', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const iconBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer' };

function SectionTitle({ icon: Icon, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
      <Icon size={16} color="#2563eb" /> {children}
    </div>
  );
}

function Field({ label, value, conf, onChange, testid }) {
  const cfg = conf ? (CONF[conf] || null) : null;
  const showBadge = cfg && conf !== 'hoog';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
        {showBadge && (
          <span data-testid={`${testid}-conf`} style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: cfg.bg, color: cfg.c, border: `1px solid ${cfg.b}` }}>{cfg.l}</span>
        )}
      </div>
      <input data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </div>
  );
}

function InfoCard({ icon: Icon, title, children, testid }) {
  return (
    <div className="cs-card" data-testid={testid} style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
        <Icon size={14} /> {title}
      </div>
      {children}
    </div>
  );
}
